import { useMutation, useQueryClient } from '@tanstack/react-query';

import { authenticationService } from '@ll-platform/frontend/core/auth/async/AuthenticationService';
import { AuthQueries } from '@ll-platform/frontend/core/auth/async/hooks';
import { useActiveUser } from '@ll-platform/frontend/core/auth/hooks/useActiveUser';
import type { User } from '@ll-platform/frontend/core/auth/types';
import { usersService } from '@ll-platform/frontend/core/users/async/UsersService';
import { UsersQueries } from '@ll-platform/frontend/core/users/async/useUsersQueries';
import type { UpdateUserDto } from '@ll-platform/frontend/core/users/types';
import { createMutationHook } from '@ll-platform/frontend/utils/factories/createMutationHook';
import type { ByIdParams } from '@ll-platform/frontend/utils/types/types';

export const useCreateUser = createMutationHook(
  usersService.create.bind(usersService),
  (queryClient) => ({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: UsersQueries._def,
      });
    },
  }),
);

/**
 * Use this mutation when updating only the preferences for less side-effects
 */
export const useUpdateUserPreferences = createMutationHook(
  (args: ByIdParams & Pick<UpdateUserDto, 'preferences'>) =>
    usersService.updateById(args),
  (queryClient) => ({
    onSuccess: (_, args) => {
      // We don't care for now about invalidating preferences in other queries as they are never read
      queryClient.setQueryData(
        AuthQueries.activeUser({}).queryKey,
        (old: User) => {
          if (!old || old._id !== args.id) {
            return old;
          }

          return {
            ...old,
            preferences: args.preferences,
          };
        },
      );
    },
  }),
);

export function useUpdateUser() {
  const queryClient = useQueryClient();
  const { activeUser } = useActiveUser();

  return useMutation({
    // typescript type inference doesn't work when used service method directly
    mutationFn: (args: ByIdParams & UpdateUserDto) =>
      usersService.updateById(args),
    onSuccess: async (_, params) => {
      queryClient.invalidateQueries({ queryKey: UsersQueries.list._def });
      queryClient.invalidateQueries({
        queryKey: UsersQueries.getById({
          userId: params.id,
        }).queryKey,
      });

      if (params.id === activeUser._id) {
        queryClient.invalidateQueries({
          queryKey: AuthQueries.activeUser({}).queryKey,
        });

        await authenticationService.reloadTokens();
      }
    },
  });
}
